/**
 * Reusable card components for the Unified Dashboard
 * Extracted for better maintainability
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { 
  TrendingUp, TrendingDown, Clock, CheckCircle2, 
  AlertTriangle, ChevronRight, ChevronDown, Sparkles, Trophy, Medal, Award, Star,
  Building2, Info
} from 'lucide-react';
import { getIndustryBenchmarks, compareToBenchmark, DATA_SOURCES } from '@/config/industryBenchmarks';

// Team Member Card
export const TeamMemberCard = ({ member, onClick }) => {
  const statusColors = {
    'exceeding': 'bg-emerald-500',
    'on-track': 'bg-blue-500',
    'at-risk': 'bg-amber-500'
  };
  
  const badgeIcons = {
    'exceeding': <Trophy className="w-3 h-3" />,
    'on-track': <Star className="w-3 h-3" />,
    'at-risk': <AlertTriangle className="w-3 h-3" />
  };
  
  return (
    <Card 
      className="glass-card cursor-pointer hover:shadow-lg transition-all group"
      onClick={() => onClick?.(member)}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-white text-sm">
              {member.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm truncate">{member.name}</p>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 hidden sm:flex">
                {badgeIcons[member.status]}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground truncate">{member.role}</p>
          </div>
          <div className="text-right">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${statusColors[member.status]}`}>
              {member.performance}
            </div>
          </div>
        </div>
        
        {/* Performance bar */}
        <div className="mt-3">
          <Progress 
            value={member.performance} 
            className="h-1.5"
          />
        </div>
        
        {/* Task stats */}
        <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            {member.tasksCompleted} done
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3 text-amber-500" />
            {member.tasksPending} pending
          </span>
        </div>
        
        {/* Hover indicator */}
        <div className="mt-2 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs text-primary flex items-center gap-1">
            View details <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

// KPI Card with optional click handler, link, and benchmark comparison
export const KPICard = ({ kpi, compact = false, onClick, linkTo, showBenchmark = false, industry }) => {
  const hasChange = typeof kpi.change === 'number' && Number.isFinite(kpi.change) && kpi.change !== 0;
  const isPositive = kpi.change >= 0;
  const TrendIcon = isPositive ? TrendingUp : TrendingDown;
  const trendColor = isPositive ? 'text-emerald-600' : 'text-red-600';
  const bgTrendColor = isPositive ? 'bg-emerald-50' : 'bg-red-50';
  
  // Get benchmark comparison if enabled
  let benchmark = null;
  let comparison = null;
  if (showBenchmark && industry) {
    const industryBenchmarks = getIndustryBenchmarks(industry);
    // Try to match KPI to a benchmark by name/id
    const kpiKey = kpi.id || kpi.name?.toLowerCase().replace(/[^a-z]/g, '_');
    benchmark = industryBenchmarks?.benchmarks?.[kpiKey];
    if (benchmark && kpi.rawValue !== undefined) {
      comparison = compareToBenchmark(kpi.rawValue, benchmark);
    }
  }
  
  const statusConfig = {
    excellent: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Excellent', icon: Trophy },
    good: { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Good', icon: CheckCircle2 },
    average: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Average', icon: Star },
    poor: { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Needs Attention', icon: AlertTriangle },
  };
  const status = comparison ? statusConfig[comparison.status] : null;
  const StatusIcon = status?.icon || Star;
  
  const CardWrapper = linkTo ? 'a' : 'div';
  const cardProps = linkTo ? { href: linkTo } : {};
  
  if (compact) {
    return (
      <Card 
        className={`glass-card ${(onClick || linkTo) ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
        onClick={onClick}
      >
        <CardWrapper {...cardProps}>
          <CardContent className="p-3">
            <p className="text-xs text-muted-foreground truncate">{kpi.name}</p>
            <p className="text-lg font-display font-bold mt-0.5">{kpi.value}</p>
            {hasChange && (
              <div className={`flex items-center gap-1 text-xs ${trendColor}`}>
                <TrendIcon className="w-3 h-3" />
                <span>{Math.abs(kpi.change)}%</span>
              </div>
            )}
          </CardContent>
        </CardWrapper>
      </Card>
    );
  }
  
  return (
    <Card 
      className={`glass-card ${(onClick || linkTo) ? 'cursor-pointer hover:shadow-lg transition-all group' : ''} ${status ? status.border : ''}`}
      onClick={onClick}
    >
      <CardWrapper {...cardProps}>
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-1">
                <p className="text-xs sm:text-sm text-muted-foreground">{kpi.name}</p>
                {benchmark && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        <p className="text-xs">{benchmark.description}</p>
                        <p className="text-[10px] text-slate-400 mt-1">
                          Industry avg: {benchmark.benchmark}{benchmark.unit}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <p className="text-xl sm:text-2xl xl:text-3xl font-display font-bold mt-1 break-words">{kpi.value}</p>
            </div>
            <div className={`p-2 rounded-full ${status?.bg || bgTrendColor}`}>
              {status ? (
                <StatusIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${status.color}`} />
              ) : (
                <TrendIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${trendColor}`} />
              )}
            </div>
          </div>
          
          {/* Benchmark status badge */}
          {status && (
            <div className="mt-2">
              <Badge className={`text-[10px] ${status.bg} ${status.color} ${status.border}`}>
                <StatusIcon className="w-2.5 h-2.5 mr-1" />
                {status.label}
                {comparison && (
                  <span className="ml-1 opacity-75">
                    ({comparison.percentDiff > 0 ? '+' : ''}{comparison.percentDiff}% vs avg)
                  </span>
                )}
              </Badge>
            </div>
          )}
          
          <div className="mt-3 sm:mt-4 flex items-center justify-between">
            {hasChange ? (
              <div className={`flex items-center gap-1 text-sm ${trendColor}`}>
                <span className="font-semibold">{isPositive ? '+' : ''}{kpi.change}%</span>
                <span className="text-xs text-muted-foreground">vs last period</span>
              </div>
            ) : <span />}
            {kpi.target && (
              <Badge variant="outline" className="text-xs whitespace-nowrap">
                Target: {kpi.target}
              </Badge>
            )}
          </div>
          
          {/* Benchmark source attribution */}
          {benchmark && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 text-[9px] text-slate-400 cursor-help">
                      <Building2 className="w-2.5 h-2.5" />
                      <span>Benchmark: {DATA_SOURCES[benchmark.source]?.name || benchmark.source}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <p className="text-xs font-medium">Industry Benchmark Source</p>
                    <p className="text-[10px] text-slate-400">{benchmark.sourceNote}</p>
                    <p className="text-[10px] mt-1">
                      Range: {benchmark.range.low} - {benchmark.range.high}{benchmark.unit}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          
          {(onClick || linkTo) && (
            <div className="mt-2 flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="text-xs text-primary flex items-center gap-1">
                View details <ChevronRight className="w-3 h-3" />
              </span>
            </div>
          )}
        </CardContent>
      </CardWrapper>
    </Card>
  );
};

// Task Item
export const TaskItem = ({ task, onToggle, compact = false, onExpand }) => {
  const priorityColors = {
    high: 'bg-red-100 text-red-700 border-red-200',
    medium: 'bg-amber-100 text-amber-700 border-amber-200',
    low: 'bg-blue-100 text-blue-700 border-blue-200'
  };
  
  return (
    <div 
      className={`flex items-start gap-3 p-2.5 sm:p-3 rounded-xl bg-slate-50/50 hover:bg-slate-100/50 transition-colors group ${onExpand ? 'cursor-pointer' : ''}`}
      onClick={onExpand ? () => onExpand(task) : undefined}
    >
      <Checkbox 
        checked={task.completed}
        onCheckedChange={() => onToggle?.(task.id)}
        className="mt-0.5"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
          {task.title}
        </p>
        {!compact && (
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            <Badge variant="outline" className={`text-[10px] ${priorityColors[task.priority]}`}>
              {task.priority}
            </Badge>
            {task.category && (
              <Badge variant="outline" className="text-[10px]">
                {task.category}
              </Badge>
            )}
            {task.dueDate && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {task.dueDate}
              </span>
            )}
          </div>
        )}
      </div>
      {onExpand && (
        <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );
};

// AI Recommendation Card
export const RecommendationCard = ({ rec, compact = false }) => {
  const [expanded, setExpanded] = useState(false);
  const priorityGradients = {
    high: 'from-red-50 to-orange-50 border-red-200/50',
    medium: 'from-amber-50 to-yellow-50 border-amber-200/50',
    low: 'from-blue-50 to-indigo-50 border-blue-200/50'
  };
  
  return (
    <div 
      className={`p-3 sm:p-4 rounded-xl bg-gradient-to-br ${priorityGradients[rec.priority]} border cursor-pointer transition-all hover:shadow-md`}
      onClick={() => setExpanded(!expanded)}
      data-testid={`recommendation-card-${rec.title?.replace(/\s+/g, '-').toLowerCase().slice(0, 30)}`}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-white/80 shadow-sm">
          <Sparkles className="w-4 h-4 text-amber-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium">{rec.title}</p>
            {rec.confidence && (
              <Badge variant="outline" className="text-[10px] bg-white/50 whitespace-nowrap">
                {rec.confidence}% confident
              </Badge>
            )}
            <ChevronDown className={`w-3.5 h-3.5 text-slate-400 ml-auto flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </div>
          {(!compact || expanded) && (
            <>
              <p className="text-xs text-muted-foreground mt-1">
                Source: {rec.source}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge className="bg-emerald-500/10 text-emerald-700 border-emerald-200/50 whitespace-nowrap">
                  Impact: {rec.impact}
                </Badge>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* Expanded methodology & data breakdown */}
      {expanded && rec.methodology && (
        <div className="mt-3 pt-3 border-t border-slate-200/60 space-y-3" data-testid="recommendation-detail">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">How We Found This</p>
            <p className="text-xs text-slate-700 leading-relaxed">{rec.methodology}</p>
          </div>
          
          {rec.dataPoints && rec.dataPoints.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Supporting Data</p>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {rec.dataPoints.map((dp, i) => (
                  <div key={`${dp?.id ?? dp?.name ?? dp?.title ?? 'dp'}-${i}`} className="bg-white/60 rounded-lg px-2.5 py-1.5 border border-slate-100">
                    <p className="text-[10px] text-slate-500">{dp.label}</p>
                    <p className="text-xs font-semibold text-slate-800">{dp.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {rec.confidenceBreakdown && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Confidence Calculation</p>
              <p className="text-[10px] text-slate-600 bg-white/50 rounded-lg px-2.5 py-1.5 border border-slate-100 font-mono">
                {rec.confidenceBreakdown}
              </p>
            </div>
          )}
        </div>
      )}
      
      {rec.methodology && !expanded && (
        <p className="text-[10px] text-slate-400 mt-2 italic">Click to see how we calculated this</p>
      )}
    </div>
  );
};
